import React, { useCallback, useMemo } from 'react';
import { useSessionStorage } from 'usehooks-ts';

import { Tab, TabsV2 } from 'common/components';

import { DEFAULT_TRADE_TAB, MOBILE_TRADE_TAB_LS_KEY } from 'common/constants';
import { useBowser, useCustomer, useElementSizeVariable, useI18n } from 'common/hooks';
import { TradeTabsType } from 'common/types';

import { memoizedComponent } from 'utils/memoizeComponent';

import './index.css';

export type TradeTabsProps = {
  tabsPersistanceInLocalStorage?: boolean;
  activeTab?: TradeTabsType;
  setActiveTab?: (tab: TradeTabsType) => void;
  className?: string;
};

const TradeTabs: React.FC<React.PropsWithChildren<TradeTabsProps>> = ({
  tabsPersistanceInLocalStorage = true,
  activeTab: activeTabParent = DEFAULT_TRADE_TAB,
  setActiveTab = () => null,
  className,
}) => {
  const { translate } = useI18n();
  const { checkFeaturesAvailability } = useCustomer();

  const { ref } = useElementSizeVariable('trade-tabs-height', true);
  const [activeTabLS, updateActiveTabLS] = useSessionStorage<TradeTabsType>(MOBILE_TRADE_TAB_LS_KEY, DEFAULT_TRADE_TAB);

  const { isMobile } = useBowser();

  const isFuturesAvailable = useMemo(() => checkFeaturesAvailability('derivatives'), [checkFeaturesAvailability]);
  const isStocksAvailable = useMemo(() => checkFeaturesAvailability('stock'), [checkFeaturesAvailability]);

  const tabs: Tab<TradeTabsType>[] = useMemo(
    () => [
      {
        label: translate.toText('mobile:combinedTrade:title:spot'),
        id: 'spot',
      },
      {
        label: translate.toText('mobile:combinedTrade:title:futures'),
        id: 'futures',
        hidden: !isFuturesAvailable,
      },
      {
        label: translate.toText('mobile:combinedTrade:title:stocks'),
        id: 'stocks',
        hidden: !isStocksAvailable,
      },
    ],
    [isFuturesAvailable, isStocksAvailable, translate],
  );

  const slidesPerView = useMemo(() => (isMobile ? tabs.filter((x) => !x.hidden).length : 'auto'), [isMobile, tabs]);
  const activeTab = useMemo(
    () => (tabsPersistanceInLocalStorage ? activeTabLS : activeTabParent),
    [activeTabLS, activeTabParent, tabsPersistanceInLocalStorage],
  );
  const updateActiveTab = useCallback(
    (tab: TradeTabsType) => {
      if (tabsPersistanceInLocalStorage) updateActiveTabLS(tab);
      setActiveTab(tab);
    },
    [tabsPersistanceInLocalStorage, setActiveTab, updateActiveTabLS],
  );

  return (
    <div ref={ref}>
      <TabsV2
        tabClassName={className}
        data-test='combined-trade-tabs'
        className='combined-trade-tabs bottom-shadow'
        slidesPerView={slidesPerView}
        onTabChange={updateActiveTab}
        tabs={tabs}
        activeTab={activeTab}
        spaceBetween={isMobile ? 0 : 16}
      />
    </div>
  );
};

export default memoizedComponent(TradeTabs);
